import { type BlockStyle, RichEditor } from '@lmc/ui-kit'
import { hasMeaningfulStoredHtml } from 'common/utils/html'
import { ViewStoredHtml } from 'components/generic/ViewStoredHtml'
import { capitalize } from 'lodash'
import type React from 'react'
import { type CSSProperties, Suspense } from 'react'
import Field from '../fields'
import { NotesLabel, TextareaWrapper } from './carePlanSharedStyles'

interface CarePlanSectionContentProps {
  fields?: object
  keys?: string | string[]
  formId?: string | null
  value?: string | null
  formFieldLabel?: string
  richText?: boolean
  onChangeHtml?: (html?: string) => void
}

const CARE_PLAN_BLOCK_STYLES: BlockStyle[] = [
  'paragraph',
  'bulletList',
  'orderedList'
]

const CarePlanSectionContent: React.FC<CarePlanSectionContentProps> = ({
  fields,
  keys,
  formId,
  value,
  formFieldLabel,
  richText,
  onChangeHtml
}) => {
  if (richText) {
    if (formId) {
      return (
        <div style={styles.formField}>
          {formFieldLabel && (
            <NotesLabel weight="medium">{formFieldLabel}</NotesLabel>
          )}
          <div style={styles.richEditorWrapper}>
            <Suspense fallback={<div>Loading...</div>}>
              <RichEditor
                html={value ?? ''}
                onChangeHtml={onChangeHtml}
                blockStyles={CARE_PLAN_BLOCK_STYLES}
              />
            </Suspense>
          </div>
        </div>
      )
    }

    return hasMeaningfulStoredHtml(value) ? (
      <div style={styles.richViewWrapper as CSSProperties}>
        <ViewStoredHtml dangerouslySetInnerHTML={{ __html: value! }} />
      </div>
    ) : null
  }

  if (formId) {
    const inputStyle = formFieldLabel ? styles.notesInput : styles.input

    return (
      <div style={styles.formField}>
        {formFieldLabel && (
          <NotesLabel weight="medium">{formFieldLabel}</NotesLabel>
        )}
        <TextareaWrapper $withNotesLabel={Boolean(formFieldLabel)}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              keys={keys}
              // @ts-ignore
              label={capitalize(keys)}
              layout="no-label"
              formId={formId}
              style={inputStyle}
              type="textarea"
              values={fields}
            />
          </Suspense>
        </TextareaWrapper>
      </div>
    )
  }

  return (
    <div
      style={styles.text as CSSProperties}
      className={value ? 'lmc-card' : ''}
    >
      {value}
    </div>
  )
}

const styles = {
  formField: {
    display: 'block'
  },
  input: {
    marginTop: 15,
    minHeight: 100,
    padding: '15px 25px 18px 25px',
    resize: 'vertical'
  },
  notesInput: {
    minHeight: 100,
    padding: '12px 16px',
    resize: 'vertical' as const,
    borderRadius: 8,
    width: '100%'
  },
  text: {
    paddingTop: 15,
    margin: '5px 0px 10px 0px',
    whiteSpace: 'pre-wrap'
  },
  richEditorWrapper: {
    marginTop: 15
  },
  richViewWrapper: {
    paddingTop: 15,
    margin: '5px 0px 10px 0px'
  }
}

export default CarePlanSectionContent
